import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

import {
  centralizedTokenService,
} from "@/client/services/token/centralized-token.service";
import { useAuthStore } from "@/client/stores/auth";
import { useTokenStore } from "@/client/stores/token";

import {
  DEFAULT_GUEST_TOKEN_BALANCE as GUEST_TOKEN_BALANCE,
  ENV_TOKEN_COSTS as TOKEN_COSTS,
} from "../../../config/env-token-config";

// Export for backward compatibility
export { GUEST_TOKEN_BALANCE };

// Export token costs for backward compatibility
export const RESUME_GENERATION_COST = TOKEN_COSTS.RESUME_GENERATION;

type TokenContextType = {
  tokenBalance: number;
  isTokenLoading: boolean;
  addTokens: (amount: number) => void;
  decrementTokens: (amount: number, reason?: string) => Promise<boolean>;
  canAfford: (amount: number) => boolean;
  getFormattedBalance: () => string;
  refreshTokenBalance: () => Promise<void>;
};

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export const useTokens = () => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error("useTokens must be used within a TokenProvider");
  }
  return context;
};

export const TokenProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLoggedIn = useAuthStore((state) => !!state.user);

  // Use Zustand store as single source of truth - no duplicate local state
  const storeBalance = useTokenStore((state) => state.tokenBalance);
  const storeIsLoading = useTokenStore((state) => state.isLoading);
  const fetchTokenBalance = useTokenStore((state) => state.fetchTokenBalance);
  const setStoreBalance = useTokenStore((state) => state.setTokenBalance);

  // Derive effective balance from store (fallback for null = not yet fetched)
  const tokenBalance = storeBalance ?? 0;

  // Track previous login state to detect guest→user transition
  const prevIsLoggedInRef = useRef<boolean | null>(null);

  // Fetch tokens on mount OR when login state transitions (guest → logged-in)
  // This handles the race where Zustand persist rehydrates AFTER the first render,
  // causing the initial fetch to run as a guest even though the user is logged in.
  useEffect(() => {
    const loginStateChanged =
      prevIsLoggedInRef.current !== null && prevIsLoggedInRef.current !== isLoggedIn;
    prevIsLoggedInRef.current = isLoggedIn;

    if (storeBalance === null || loginStateChanged) {
      // Force refresh when transitioning from guest → logged-in so we don't
      // show the stale guest balance (0) to authenticated users.
      void fetchTokenBalance(isLoggedIn, loginStateChanged);
    }
  }, [isLoggedIn, fetchTokenBalance, storeBalance]);

  const addTokens = useCallback((amount: number) => {
    const current = useTokenStore.getState().tokenBalance ?? 0;
    setStoreBalance(current + amount);
  }, [setStoreBalance]);

  const decrementTokens = useCallback(
    async (amount: number, _reason?: string): Promise<boolean> => {
      try {
        const result = await centralizedTokenService.consumeTokens(
          amount,
          isLoggedIn,
          "RESUME_GENERATION",
        );

        if (result.success) {
          setStoreBalance(result.newBalance);
          return true;
        } else {
          console.error("Token deduction failed:", result.error);
          return false;
        }
      } catch (error) {
        console.error("Error deducting tokens:", error);
        return false;
      }
    },
    [isLoggedIn, setStoreBalance],
  );

  const canAfford = useCallback(
    (amount: number): boolean => {
      return tokenBalance >= amount;
    },
    [tokenBalance],
  );

  const getFormattedBalance = useCallback((): string => {
    return tokenBalance.toString();
  }, [tokenBalance]);

  const refreshTokenBalance = useCallback(async () => {
    try {
      await fetchTokenBalance(isLoggedIn, true);
    } catch (error) {
      console.error("Failed to refresh token balance:", error);
    }
  }, [isLoggedIn, fetchTokenBalance]);

  const contextValue = useMemo(
    () => ({
      tokenBalance,
      isTokenLoading: storeIsLoading && storeBalance === null,
      addTokens,
      decrementTokens,
      canAfford,
      getFormattedBalance,
      refreshTokenBalance,
    }),
    [
      tokenBalance,
      storeIsLoading,
      storeBalance,
      addTokens,
      decrementTokens,
      canAfford,
      getFormattedBalance,
      refreshTokenBalance,
    ],
  );

  return <TokenContext.Provider value={contextValue}>{children}</TokenContext.Provider>;
};

export const PDF_EXPORT_COST = TOKEN_COSTS.PDF_EXPORT;
export const WORD_EXPORT_COST = TOKEN_COSTS.WORD_EXPORT;
export const ANALYTICS_COST = TOKEN_COSTS.ANALYTICS;
